/**
 * CORE-PF-01 + CORE-PF-02 Performance page.
 *
 * Chans's 2026-09-15 rebuild spec §11:
 *   - PF-01: merge Monthly Recaps into KPI Diagnostics. One Performance
 *     page. Recap generator triggered from that page. Markdown download
 *     preserved.
 *   - PF-02: rename "KPI Diagnostics" to "Performance" in the left nav.
 *
 * This ticket ships the merge as a compose: the existing daily driver
 * (`<KpiDiagnosticsTab>`) sits at the top, and the existing recap
 * generator + history (`<MonthlyRecapsTab>`) lives directly below in a
 * collapsible section that starts CLOSED. Its generator button and
 * markdown download behaviour travel unchanged.
 *
 * MVP-first: no interior extraction, no new services. Chans's "no
 * enhancements before the foundation is up" rule lands the shape as
 * spec, without rewriting the recap flow.
 */
import { ChevronRight } from 'lucide-react';

import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { KpiDiagnosticsTab } from '@/components/google-ads/KpiDiagnosticsTab';
import { MonthlyRecapsTab } from '@/components/google-ads/MonthlyRecapsTab';
import { useTenantStore } from '@/stores/useTenantStore';

interface PerformancePageProps {
  tenantId?: string | null;
}

export function PerformancePage({ tenantId: tenantIdProp }: PerformancePageProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = (tenantIdProp === undefined ? storeTenant : tenantIdProp) ?? null;

  // CORE-G-2.b: purpose statement lives on the outer GoogleAdsOpsPage
  // wrapper (one H1 + description per Ads Tools section, sectionCopy).
  // No inline header here, so the operator reads the title once.
  if (!tenantId) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>No workspace selected</CardTitle>
            <CardDescription>
              Pick a workspace to see its Performance view.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI diagnostics is the always-on view: last 30 days vs the
          previous period, with the four KPIs Art wired to fire. */}
      <KpiDiagnosticsTab tenantId={tenantId} />

      {/* Monthly recap generator + history. Collapsed by default because
          it is a monthly cadence, not a daily one. The Generate button
          and the markdown/HTML/PDF download flow travel inside the
          existing MonthlyRecapsTab component — no reimplementation. */}
      <details className="rounded-md border border-border bg-warm-bg">
        <summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-semibold">
          <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
          Monthly recap
          <span className="ml-2 font-normal text-muted-foreground">
            generate a new recap or download a past one
          </span>
        </summary>
        <div className="border-t border-border p-4">
          <MonthlyRecapsTab tenantId={tenantId} />
        </div>
      </details>

      {/* CORE-PF-02: this used to carry an operator-visible "Renamed
          from KPI Diagnostics (CORE-PF-01 / PF-02)" line. Ticket IDs and
          historical page names in live copy read as developer chatter;
          the header purpose statement already names what this page is.
          The rename history stays in this module's docstring for anyone
          who needs it. */}
    </div>
  );
}

export default PerformancePage;
